import type { CoachingHistoryResponsePagedData, CoachResponse } from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import { CoachHistory } from 'components/Db/Profile/Coach/CoachHistory';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';

interface CoachHistoryProps extends IGenericPageProps {
  coach?: CoachResponse;
  coachHistory?: CoachingHistoryResponsePagedData;
}

const CoachHistoryPage = ({
  coach,
  coachHistory,
  siteData,
  ...props
}: CoachHistoryProps) => {
  const title = `${coach?.fullName} Coaching History | On3.com`;
  const description = `Explore the coaching history of ${coach?.fullName}, including teams, positions, and years`;

  return (
    <PageWrapper
      {...props}
      description={description}
      siteData={siteData}
      title={title}
    >
      <DatabaseLayout>
        <CoachHistory coach={coach} list={coachHistory?.list} />
      </DatabaseLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { id } = ctx.query;
  const coachId = id?.toString().split('-').pop();

  try {
    const [coach, coachHistory] = await Promise.allSettled([
      proxyApi
        .get<CoachResponse>(`/public/rdb/v1/coaches/${coachId}/profile`)
        .then((r) => r.data),

      proxyApi
        .get<CoachingHistoryResponsePagedData>(
          `/public/rdb/v1/coaches/${coachId}/history`,
        )
        .then((r) => r.data),
    ]);

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      coach: coach.status === 'fulfilled' ? coach.value : {},
      coachHistory:
        coachHistory.status === 'fulfilled' ? coachHistory.value : {},
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: {} };
  }
};

export default withValidUrl(CoachHistoryPage);
